import { format } from 'date-fns';
import { X, Loader2, Check, AlertTriangle, User, Truck, Wrench } from 'lucide-react';
import type { ReassignmentAnalysis } from '../../../lib/api';

// ─── Phase 47 (GH #104): reassignment dialog ────────────────────────────────
//
// A real modal (backdrop + centred card, mirroring ScheduleModal). Three
// states drive what it shows:
//   - state.result      → outcome summary (what actually moved + gaps) + Done
//   - state.previewError → the dry-run failed; show error + Cancel
//   - state.preview      → conflict analysis (Step 1) + A/B/C buttons (Step 2)
//   - otherwise          → loading spinner while the preview is in flight
export function ReassignmentDialog({
  state,
  fromLabel,
  isPhase,
  isExecuting,
  onTransfer,
  onClear,
  onCancel,
  onDone,
}: {
  state: {
    newStartDate: string;
    newEndDate: string;
    preview: ReassignmentAnalysis | null;
    previewError: string | null;
    executeError: string | null;
    result: ReassignmentAnalysis | null;
  };
  fromLabel: string;
  isPhase: boolean;
  isExecuting: boolean;
  onTransfer: () => void;
  onClear: () => void;
  onCancel: () => void;
  onDone: () => void;
}) {
  const toLabel =
    format(new Date(state.newStartDate), 'd MMM') +
    (state.newEndDate && state.newEndDate !== state.newStartDate
      ? `–${format(new Date(state.newEndDate), 'd MMM')}`
      : '');

  const subject = isPhase ? 'this phase' : 'this job';

  return (
    <div role="dialog" aria-modal="true" aria-label="Reassign resources">
      {/* Backdrop — clicking cancels (only when not executing). */}
      <div
        className="fixed inset-0 bg-black/30 z-[60]"
        onClick={state.result ? onDone : isExecuting ? undefined : onCancel}
      />
      {/* Card */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-md bg-white rounded-xl shadow-2xl border border-neutral-200 max-h-[88vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-neutral-200">
          <div>
            <h2 className="text-[15px] text-neutral-900 leading-snug">Reassign resources</h2>
            <p className="text-[12px] text-neutral-500 mt-0.5">
              Moving {subject} from <strong>{fromLabel}</strong> to <strong>{toLabel}</strong>
            </p>
          </div>
          <button
            onClick={state.result ? onDone : onCancel}
            disabled={isExecuting}
            className="p-1.5 hover:bg-neutral-100 rounded-md text-neutral-400 transition-colors mt-0.5 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-3">
          {/* ── Result view (after a successful transfer/clear) ── */}
          {state.result ? (
            <ReassignmentResult result={state.result} />
          ) : state.previewError ? (
            <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {state.previewError}
            </p>
          ) : !state.preview ? (
            <p className="text-[12px] text-neutral-500 italic flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking for clashes…
            </p>
          ) : (
            <ReassignmentPreview preview={state.preview} />
          )}

          {state.executeError && (
            <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {state.executeError}
            </p>
          )}
        </div>

        {/* Footer — actions depend on the view. */}
        <div className="px-5 py-3 border-t border-neutral-200">
          {state.result ? (
            <div className="flex justify-end">
              <button
                onClick={onDone}
                className="px-4 py-1.5 rounded-md bg-[#F26522] text-white text-[12px] hover:bg-[#d9561d] transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <button
                onClick={onTransfer}
                disabled={isExecuting || !state.preview || !!state.previewError}
                className="flex items-center justify-center gap-1.5 w-full px-4 py-2 rounded-md bg-[#F26522] text-white text-[12px] hover:bg-[#d9561d] disabled:opacity-50 transition-colors"
              >
                {isExecuting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Transfer non-conflicting (recommended)
              </button>
              <button
                onClick={onClear}
                disabled={isExecuting || !state.preview || !!state.previewError}
                className="w-full px-4 py-2 rounded-md border border-red-300 text-red-700 text-[12px] hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                Move &amp; clear all resources
              </button>
              <button
                onClick={onCancel}
                disabled={isExecuting}
                className="w-full px-4 py-1.5 rounded-md border border-neutral-300 text-neutral-700 text-[12px] hover:bg-neutral-100 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Step-1 conflict analysis body. Lists per-resource conflicts (plain-English
// reasons) and the would-transfer counts. Materials/skills/documents are
// untouched — copy notes that.
function ReassignmentPreview({ preview }: { preview: ReassignmentAnalysis }) {
  const totalConflicts =
    preview.conflicts.staff.length +
    preview.conflicts.vehicles.length +
    preview.conflicts.equipment.length;

  const staffReason = (c: ReassignmentAnalysis['conflicts']['staff'][number]) => {
    if (c.reason === 'leave') return 'on leave';
    if (c.reason === 'out_of_range') return 'outside the new dates';
    if (c.reason === 'daily_rest_violation') return c.detail ?? 'insufficient daily rest';
    if (c.reason === 'weekly_rest_violation') return c.detail ?? 'insufficient weekly rest';
    return `double-booked on ${c.conflicting_job_name ?? 'another job'}${
      c.conflicting_ion ? ` (${c.conflicting_ion})` : ''
    }`;
  };
  const vehicleReason = (c: ReassignmentAnalysis['conflicts']['vehicles'][number]) => {
    if (c.reason === 'out_of_range') return 'outside the new dates';
    if (c.reason === 'unavailable')
      return `unavailable${c.unavailability_reason ? ` (${c.unavailability_reason.replaceAll('_', ' ')})` : ''}`;
    return `double-booked on ${c.conflicting_job_name ?? 'another job'}${
      c.conflicting_ion ? ` (${c.conflicting_ion})` : ''
    }`;
  };
  const equipmentReason = (c: ReassignmentAnalysis['conflicts']['equipment'][number]) =>
    c.reason === 'out_of_range' ? 'outside the new dates' : 'no spare capacity';

  return (
    <div className="space-y-3">
      {/* Would-transfer summary */}
      <div className="rounded-md bg-neutral-50 border border-neutral-200 px-3 py-2">
        <p className="text-[12px] text-neutral-700">
          <strong>
            {preview.moved.staff} staff member assignment{preview.moved.staff === 1 ? '' : 's'}
          </strong>
          ,{' '}
          <strong>
            {preview.moved.vehicles} vehicle assignment{preview.moved.vehicles === 1 ? '' : 's'}
          </strong>{' '}
          and{' '}
          <strong>
            {preview.moved.equipment} equipment item assignment
            {preview.moved.equipment === 1 ? '' : 's'}
          </strong>{' '}
          can be cleanly moved in line with the new schedule.
        </p>
      </div>

      {totalConflicts === 0 ? (
        <p className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-start gap-1.5">
          <Check className="w-3.5 h-3.5 shrink-0 mt-px" />
          All staff, vehicles and equipment will move cleanly. Nothing clashes on the new dates.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-[12px] text-amber-900 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            {totalConflicts} item{totalConflicts === 1 ? '' : 's'} can&apos;t transfer and will be
            released:
          </p>

          {preview.conflicts.staff.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-neutral-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                <User className="w-3 h-3" /> Staff
              </p>
              <ul className="text-[11px] text-neutral-700 space-y-0.5 ml-1">
                {preview.conflicts.staff.map((c) => (
                  <li key={c.id}>
                    <strong>{c.staff_name}</strong> — {staffReason(c)} on{' '}
                    {format(new Date(c.date), 'EEE d MMM')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.conflicts.vehicles.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-neutral-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                <Truck className="w-3 h-3" /> Vehicles
              </p>
              <ul className="text-[11px] text-neutral-700 space-y-0.5 ml-1">
                {preview.conflicts.vehicles.map((c) => (
                  <li key={c.id}>
                    <strong>{c.registration}</strong> — {vehicleReason(c)} on{' '}
                    {format(new Date(c.date), 'EEE d MMM')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.conflicts.equipment.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-neutral-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                <Wrench className="w-3 h-3" /> Equipment
              </p>
              <ul className="text-[11px] text-neutral-700 space-y-0.5 ml-1">
                {preview.conflicts.equipment.map((c) => (
                  <li key={c.id}>
                    <strong>{c.equipment_name}</strong> — {equipmentReason(c)} on{' '}
                    {format(new Date(c.date), 'EEE d MMM')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-neutral-400 italic">
        Materials, skills and documents stay attached.
      </p>
    </div>
  );
}

// Post-execute outcome summary — what actually moved + any gaps left short.
function ReassignmentResult({ result }: { result: ReassignmentAnalysis }) {
  const couldntStaff = result.conflicts.staff.length;
  const couldntVehicles = result.conflicts.vehicles.length;
  const couldntEquipment = result.conflicts.equipment.length;
  const couldntTotal = couldntStaff + couldntVehicles + couldntEquipment;

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-emerald-700 flex items-center gap-1.5">
        <Check className="w-4 h-4" /> Resources reassigned
      </p>
      <p className="text-[12px] text-neutral-700">
        Moved <strong>{result.moved.staff}</strong> staff, <strong>{result.moved.vehicles}</strong>{' '}
        vehicle{result.moved.vehicles === 1 ? '' : 's'} and{' '}
        <strong>{result.moved.equipment}</strong> equipment item
        {result.moved.equipment === 1 ? '' : 's'} to the new dates.
      </p>

      {couldntTotal > 0 && (
        <p className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          {couldntTotal} item{couldntTotal === 1 ? '' : 's'} couldn&apos;t transfer and{' '}
          {couldntTotal === 1 ? 'was' : 'were'} released.
        </p>
      )}

      {result.gaps.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-neutral-600 mb-1">Days now short-handed:</p>
          <ul className="text-[11px] text-neutral-700 space-y-0.5 ml-1">
            {result.gaps.map((g) => {
              const bits: string[] = [];
              if (g.missing_staff) bits.push(`${g.missing_staff} staff`);
              if (g.missing_vehicles) bits.push(`${g.missing_vehicles} vehicle(s)`);
              if (g.missing_equipment) bits.push(`${g.missing_equipment} equipment`);
              return (
                <li key={g.date}>
                  <strong>{format(new Date(g.date), 'EEE d MMM')}</strong>
                  {bits.length > 0 ? ` — missing ${bits.join(', ')}` : ''}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
